import { HSpacer, IconButton, Search, Text, TopAppBar, VSpacer } from '@/components/DesignSystem';
import { FilterBar } from '@/components/DesignSystem/Toolbar/FilterBar';
import { FilterChip } from '@/components/DesignSystem/Toolbar/FilterChip';
import { FilterSheet } from '@/components/DesignSystem/Toolbar/FilterSheet';
import { Filter, FilterSelections } from '@/components/DesignSystem/Toolbar/interfaces';
import { InfiniteScroll } from '@/components/shared/InfiniteScroll';
import { OrderDetailsModal } from '@/components/shared/OrderDetailsModal';
import { ManualPointsHistoryDetailsModal } from './PointsActivity/ManualPointsHistoryDetailsModal';
import { QueryKeys } from '@/constants/QueryKeys';
import { Routes } from '@/constants/Routes';
import { useDashboard } from '@/hooks/useDashboard';
import { useSearch } from '@/hooks/useSearch';
import { DashboardApi } from '@/utilities/api/DashboardApi';
import { OrderTransactionsApi } from '@/utilities/api/OrderTransactionsApi';
import { PointsHistoryEndpoint } from '@api/endpoints/PointsHistoryEndpoint';
import { ApiSubwallet } from '@api/interfaces';
import { ApiPointsHistory } from '@api/interfaces/ApiPointsHistory';
import ArrowBack from '@mui/icons-material/ArrowBack';
import { CircularProgress, Container, Stack, useTheme } from '@mui/material';
import { formatMediumDate } from '@shared/utilities';
import { filterToViewableCustomers, getAccountName } from '@shared/utilities/UserUtilities';
import i18next from 'i18next';
import React, { useMemo, useState } from 'react';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDashboardNavigate } from './helpers';
import { PointsActivityRow, ResolvedOrderTransaction } from './interfaces';
import { PointsActivityItem } from './PointsActivity/PointsActivityItem';
import { AboutAccountRolesBanner } from '@/pages/CustomerDashboard/LinkedAccounts';
import { CIBPageName } from "@shared/enums";

export const PointsActivityPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const accessToken = searchParams.get('accessToken') ?? params.accessToken;
  const navigateToPage = useDashboardNavigate();
  const { search: searchTerm, setSearch: setSearchTerm, debouncedSearch } = useSearch(3);
  const [filterSelections, setFilterSelections] = useState<FilterSelections>(() => {
    const map = new Map();
    map.set('transaction-type', new Set());
    return map;
  });
  const [showCustomerFilterSheet, setShowCustomerFilterSheet] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<ResolvedOrderTransaction | null>(null);
  const [selectedPointsHistory, setSelectedPointsHistory] = useState<ApiPointsHistory | null>(null);

  const { data: dashboardData } = useDashboard(accessToken);

  const { data: latestProcessedOrderDate } = useQuery(
    [QueryKeys.GET_LATEST_PROCESSED_ORDER_DATE, dashboardData?.retailer.id],
    () => OrderTransactionsApi.getLatestProcessedOrderDate({
      accessToken,
      retailerId: dashboardData!.retailer.id,
    }),
    { enabled: !!dashboardData?.retailer.id },
  );

  const { data: transactionSubtypes } = useQuery(
    [QueryKeys.GET_ORDERS_HAS_TRANSACTION_SUBTYPES, accessToken, dashboardData?.retailer.id],
    () => OrderTransactionsApi.hasTransactionSubtypes({
      accessToken,
      retailerId: dashboardData!.retailer.id,
    }),
    { enabled: !!dashboardData?.retailer.id },
  );

  const farmer = dashboardData?.farmer;
  const linkedAccounts = dashboardData?.linkedAccounts || [];
  const retailer = dashboardData?.retailer;
  const brandColor = retailer?.brandColor ?? theme.palette.primary.main;
  const balances = dashboardData?.balances || [];

  const subwallets: ApiSubwallet[] = useMemo(() => (
    [...new Map(balances.map(({ subwallet }) => [subwallet.id, subwallet])).values()]
  ), [balances]);

  const customerIds = Array.from(filterSelections?.get('customer') ?? []);
  const transactionType = filterSelections?.get('transaction-type')?.values().next().value ?? 'all';

  const activityQuery = useMemo(() => {
    const query: PointsHistoryEndpoint.List.Query = {
      limit: 20,
      ...(accessToken ? { accessToken } : {}),
    };
    if (debouncedSearch?.trim()) {
      query.search = debouncedSearch.trim();
    }
    if (transactionType !== 'all') {
      query.transactionType = transactionType as 'order' | 'scale-ticket' | 'settlement';
    }
    if (customerIds.length > 0) {
      query.customerIds = customerIds;
    }
    return query;
  }, [
    accessToken,
    debouncedSearch,
    transactionType,
    customerIds,
  ]);

  const selectionsString = useMemo(
    () =>
      JSON.stringify(
        Array.from(filterSelections ?? []).map(([, values]) => Array.from(values)),
      ),
    [filterSelections],
  );

  const {
    data: activityData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
  } = useInfiniteQuery(
    [QueryKeys.GET_DASHBOARD_ORDERS, farmer?.id, JSON.stringify(activityQuery), selectionsString],
    async ({ pageParam = 0 }) => (
      DashboardApi.listOrderPointActivity(farmer!.id, {
        ...activityQuery,
        page: pageParam,
      })
    ),
    {
      enabled: !!farmer?.id,
      getNextPageParam: (lastPage) =>
        lastPage.page < lastPage.lastPage ? lastPage.page + 1 : undefined,
      keepPreviousData: true,
    },
  );

  const activityRows: PointsActivityRow[] = activityData?.pages?.flatMap((page) => (
    page.data.map((row): PointsActivityRow => {
      if (row.rowType === 'order') {
        return { rowType: 'order', order: row.order as ResolvedOrderTransaction };
      }
      return { rowType: 'points-history', pointsHistory: row.pointsHistory };
    })
  )) ?? [];

  const shouldShowTransactionFilter = !!(
    transactionSubtypes?.hasScaleTicket || transactionSubtypes?.hasSettlement
  );

  const transactionTypeFilter = useMemo(() => {
    const options: { id: string; label: string }[] = [
      { id: 'order', label: t($ => $.common.order.order) },
    ];

    if (transactionSubtypes?.hasScaleTicket) {
      options.push({
        id: 'scale-ticket',
        label: t($ => $.customerDashboard.pointsActivity.filters.scaleTicket),
      });
    }

    if (transactionSubtypes?.hasSettlement) {
      options.push({ id: 'settlement', label: t($ => $.common.order.settlement) });
    }

    return {
      id: 'transaction-type',
      label: t($ => $.customerDashboard.pointsActivity.filters.transactionType),
      options,
      selectionMethod: 'single-select' as const,
    };
  }, [t, transactionSubtypes]);

  const availableCustomers = useMemo(() => {
    const customers: { id: string; label: string; subline?: string }[] = [];

    const viewableCustomers = !farmer
      ? []
      : filterToViewableCustomers(
        farmer,
        linkedAccounts,
      );
    if (viewableCustomers.length > 1) {
      viewableCustomers.forEach((account) => {
        const accountName = getAccountName(account);
        customers.push({
          id: account.id,
          label: accountName.length > 30 ? `${accountName.substring(0, 30)}...` : accountName,
          subline: account.externalId ? `ID ${account.externalId}` : undefined,
        });
      });
    }

    return customers;
  }, [farmer, linkedAccounts]);

  const shouldShowCustomerFilter = availableCustomers.length >= 2;

  const getRowDate = (row: PointsActivityRow) =>
    row.rowType === 'order' ? row.order.orderDate : row.pointsHistory.orderDate;

  const sortedRows = useMemo(() => (
    [...activityRows].sort((a, b) => {
      const dateDiff = getRowDate(b).localeCompare(getRowDate(a));
      if (dateDiff !== 0) {
        return dateDiff;
      }
      if (a.rowType === 'order' && b.rowType === 'order') {
        return (b.order.externalId || '').localeCompare(a.order.externalId || '');
      }
      return 0;
    })
  ), [activityRows]);

  const groupedByDate = useMemo(() => {
    const grouped: { [key: string]: PointsActivityRow[] } = {};

    sortedRows.forEach((row) => {
      const dateKey = new Date(getRowDate(row)).toISOString().slice(0, 10);
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(row);
    });

    return Object.entries(grouped).sort(([dateA], [dateB]) => dateB.localeCompare(dateA));
  }, [sortedRows]);

  const renderRow = (row: PointsActivityRow) => {
    if (!farmer) {
      return null;
    }
    const id = row.rowType === 'order' ? row.order.id : row.pointsHistory.id;
    const handleClick = () => {
      if (row.rowType === 'order') {
        setSelectedOrder(row.order);
      } else {
        setSelectedPointsHistory(row.pointsHistory);
      }
    };
    if (row.rowType === 'order') {
      return (
        <PointsActivityItem
          brandColor={brandColor}
          farmer={farmer}
          key={id}
          linkedAccounts={linkedAccounts}
          onClick={handleClick}
          order={row.order}
        />
      );
    }
    return (
      <PointsActivityItem
        brandColor={brandColor}
        farmer={farmer}
        key={id}
        linkedAccounts={linkedAccounts}
        onClick={handleClick}
        pointsHistory={row.pointsHistory}
      />
    );
  };

  if (!dashboardData) {
    return (
      <Container>
        <Stack alignItems='center'>
          <VSpacer size='14' />
          <CircularProgress />
        </Stack>
      </Container>
    );
  }

  const Header = (
    <TopAppBar
      isStatic
      leftAccessory={(
        <IconButton
          onClick={() => navigateToPage(Routes.CUSTOMER_DASHBOARD, accessToken)}
          sx={{ color: 'white' }}
          testID="back-button"
        >
          <ArrowBack />
        </IconButton>
      )}
      rightAccessory={<HSpacer size="7" />}
      sx={{ bgcolor: brandColor }}
      testID="top-app-bar"
    >
      <Text category="title-large" sx={{ color: 'white' }}>
        {t($ => $.customerDashboard.pointsActivity.title)}
      </Text>
    </TopAppBar>
  );

  return (
    <>
      <Stack
        bgcolor={theme.palette.background.default}
        flexGrow={1}
        height="auto"
        maxWidth="599px"
        minHeight="100vh"
        minWidth="360px"
      >
        {Header}
        <Stack bgcolor={theme.palette.background.default} flexGrow={1}>
          {!!latestProcessedOrderDate && (
            <>
              <VSpacer size="4" />
              <Stack alignItems="center">
                <Text category="body-xsmall">
                  {t(
                    $ => $.common.updated,
                    { date: latestProcessedOrderDate },
                  )}
                </Text>
              </Stack>
            </>
          )}
          <VSpacer size="4" />
          <Stack px="16px">
            {!!farmer?.accountGroupRole && !!linkedAccounts?.length && (
               <>
              <AboutAccountRolesBanner
                brandColor={brandColor}
                customerRole={farmer.accountGroupRole}
                linkedAccounts={linkedAccounts}
                pageName={CIBPageName.CRD_PointsActivity}
                showMobileVersion={true}
              />
                <VSpacer size="4" />
              </>
            )}
          </Stack>
          <VSpacer size="4" />
          <Stack px="16px" spacing={2}>
            <Search
              fullWidth
              onChangeText={setSearchTerm}
              placeholder={t($ => $.common.search.searchById)}
              testID="points-activity-search"
              value={searchTerm}
            />
            <Stack direction="row" gap="8px">
            {shouldShowCustomerFilter && (
              <>
                <FilterChip
                  filter={{
                    id: 'customer',
                    label: t($ => $.common.customer),
                    options: availableCustomers,
                    selectionMethod: 'multi-select',
                    truncateLabel: true,
                  } as Filter}
                  onClick={() => setShowCustomerFilterSheet(true)}
                  selection={filterSelections.get('customer')}
                  testID="customer-filter-chip"
                />
                <FilterSheet
                  filters={[{
                    id: 'customer',
                    label: t($ => $.common.customer),
                    options: availableCustomers,
                    selectionMethod: 'multi-select',
                    truncateLabel: true,
                  } as Filter]}
                  onApply={(selections) => {
                    setFilterSelections(selections);
                    setShowCustomerFilterSheet(false);
                  }}
                  onClose={() => setShowCustomerFilterSheet(false)}
                  open={showCustomerFilterSheet}
                  selections={filterSelections}
                  testID="customer-filter-sheet"
                  themeColor={brandColor}
                  title={t($ => $.common.customer)}
                />
              </>
            )}
            {(!transactionSubtypes || shouldShowTransactionFilter) && (
              <Stack
                sx={!transactionSubtypes
                  ? { opacity: 0.5, pointerEvents: 'none' }
                  : undefined
                }
              >
                <FilterBar
                  filters={[transactionTypeFilter as Filter]}
                  hideAllFilter={true}
                  onChange={setFilterSelections}
                  selections={filterSelections}
                  showClearAllButton={false}
                  testID="transaction-type-filter"
                  themeColor={brandColor}
                />
              </Stack>
            )}
          </Stack>
          </Stack>
          <VSpacer size="4" />
          <Stack px="16px">
            <InfiniteScroll
              fetchMoreItems={fetchNextPage}
              hasMore={hasNextPage ?? false}
              height="calc(100vh - 300px)"
              isLoading={isFetchingNextPage}
            >
              {isLoading && activityRows.length === 0 ? (
                <Stack alignItems="center" py="48px">
                  <CircularProgress />
                </Stack>
              ) : activityRows.length === 0 ? (
                <Stack alignItems="center" py="48px">
                  <Text category="body-large">
                    {t($ => $.customerDashboard.pointsActivity.emptyState)}
                  </Text>
                </Stack>
              ) : (
                <Stack>
                  {groupedByDate.map(([dateKey, rows]) => (
                    <Stack key={dateKey}>
                      <Text
                        category="body-xlarge"
                        pb="12px"
                        pt="16px"
                        testID="points-activity-date"
                      >
                        {formatMediumDate(dateKey, i18next)}
                      </Text>
                      <Stack gap="8px" pt="8px">
                        {rows.map(renderRow)}
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </InfiniteScroll>
          </Stack>
        </Stack>
        {!!selectedOrder && !!farmer && (
          <OrderDetailsModal
            accessToken={accessToken}
            brandColor={brandColor}
            forceMobileView
            onClose={() => setSelectedOrder(null)}
            orderId={selectedOrder.id}
            subwallets={subwallets}
          />
        )}
        {!!selectedPointsHistory && (
          <ManualPointsHistoryDetailsModal
            brandColor={brandColor}
            forceMobileView
            onClose={() => setSelectedPointsHistory(null)}
            pointsHistory={selectedPointsHistory}
          />
        )}
      </Stack>
    </>
  );
};
